import type {
  CapabilitiesResponse,
  CeoBreakdownResponse,
  CeoQuery,
  CeoTrendsResponse,
  CrmCohort,
  CrmHealth,
  CrmPanelId,
  CrmQuery,
  CrmResponse,
  DataSetId,
  FunnelFigure,
  MetricResult,
  MissingInput,
  Snapshot,
} from '@sr-ceo/contracts';
import { CEO_METRIC_IDS } from '@sr-ceo/contracts';
import Chart from 'chart.js/auto';
import { api, BridgeError, BridgeUnavailable, Unauthenticated } from './api.ts';

// Which gold the page shows, remembered in this browser only (a per-viewer convenience). Our gold
// unless DS gold was picked here (decided 14 October: management looks at our gold).
const DATA_SET_KEY = 'sr-ceo-data-set';
let dataSet: DataSetId = (() => {
  try {
    return localStorage.getItem(DATA_SET_KEY) === 'ds' ? 'ds' : 'ours';
  } catch {
    return 'ours';
  }
})();
export const currentDataSet = () => dataSet;
export function chooseDataSet(id: DataSetId) {
  dataSet = id;
  try {
    localStorage.setItem(DATA_SET_KEY, id);
  } catch {
    // storage blocked: the choice lasts until the page reloads
  }
}

export const NOT_BUILT = 'Not built yet: this part of the dashboard comes in a later phase.';
const UNAVAILABLE = 'Unavailable';
const MAX_RANGE_DAYS = 366;

const pane = (id: string) => document.querySelector<HTMLElement>(`[data-pane="${id}"]`);

/** Puts "Unavailable" into every place the mock showed data, without changing the layout. */
export function markUnavailable(paneId: string, reason: string) {
  const root = pane(paneId);
  if (!root) {
    return;
  }
  for (const el of root.querySelectorAll<HTMLElement>('.kpi .value')) {
    setUnavailable(el, reason);
  }
  // The mock's delta line under each card ("● no comparison period" style).
  for (const el of root.querySelectorAll<HTMLElement>('.kpi .delta')) {
    el.className = 'delta flat';
    el.textContent = '● comparison unavailable';
  }
  for (const tbody of root.querySelectorAll('tbody')) {
    const cols = tbody.closest('table')?.querySelectorAll('thead th').length ?? 1;
    if (!tbody.rows.length) {
      tbody.innerHTML = `<tr class="unavailable-row"><td colspan="${cols}" title="${escapeAttr(reason)}">${UNAVAILABLE}</td></tr>`;
      continue;
    }
    // Rows that carry only a label (e.g. funnel stage names): mark the measured cells.
    for (const row of tbody.rows) {
      const first = row.cells[1];
      if (first && !first.textContent?.trim()) {
        first.textContent = UNAVAILABLE;
        first.classList.add('unavailable');
        first.title = reason;
      }
    }
  }
  for (const canvas of root.querySelectorAll('canvas')) {
    canvas.parentElement?.classList.add('is-unavailable');
    canvas.parentElement?.setAttribute('title', reason);
  }
  for (const el of root.querySelectorAll<HTMLElement>('.alert-list, .funnel, .health, .timeline')) {
    el.innerHTML = `<div class="unavailable-note" title="${escapeAttr(reason)}">${UNAVAILABLE}</div>`;
  }
}

/** The mock's own "not available" value style (.kpi .value.na). */
function setUnavailable(el: HTMLElement, reason: string) {
  el.textContent = UNAVAILABLE;
  el.classList.add('na');
  el.title = reason;
}

/** A failure that may pass by itself: no answer (or one cut off part-way), or a 5xx. Not a refused
 *  request (4xx, e.g. a filter the data set can't apply), nor a complete answer of the wrong shape:
 *  trying those again every refresh would only blank the pane each time. */
const mayPass = (err: unknown) => err instanceof BridgeUnavailable || (err instanceof BridgeError && err.status >= 500);

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** The Date filter opens on Last 30 Days, not the mock's Today: a single day is often empty on
 *  real data (Last 7 Days from 13 October, Last 30 Days from 15 October). DS gold has it too. */
function startOnLastThirtyDays(el: HTMLSelectElement | null) {
  if (el) {
    el.value = 'd30';
  }
}

/** The reporting range for a date preset, as half-open UTC days (CEO sheet Q22), or a reason
 * why it can't be sent. */
export function rangeFor(
  preset: string,
  today = new Date(),
  custom?: { from?: string; to?: string },
): { from?: string; to: string } | { unsupported: string } {
  const day = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const D = 86_400_000;
  const iso = (ms: number) => new Date(ms).toISOString().replace('.000Z', 'Z');
  const tomorrow = day + D;
  switch (preset) {
    case 'today':
      return { from: iso(day), to: iso(tomorrow) };
    case 'yday':
      return { from: iso(day - D), to: iso(day) };
    case 'd7':
      return { from: iso(day - 6 * D), to: iso(tomorrow) };
    case 'd30':
      return { from: iso(day - 29 * D), to: iso(tomorrow) };
    case 'd90':
      return { from: iso(day - 89 * D), to: iso(tomorrow) };
    case 'month':
      return { from: iso(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)), to: iso(tomorrow) };
    case 'all':
      return { to: iso(tomorrow) }; // no from: from the first record
    case 'custom': {
      if (!custom?.from || !custom.to) {
        return { unsupported: 'Pick a From and a To date.' };
      }
      const from = Date.parse(`${custom.from}T00:00:00Z`);
      const to = Date.parse(`${custom.to}T00:00:00Z`) + D;
      if (!(to > from)) {
        return { unsupported: 'From must be on or before To.' };
      }
      if (to - from > MAX_RANGE_DAYS * D) {
        return { unsupported: `A range can cover at most ${MAX_RANGE_DAYS} days.` };
      }
      return { from: iso(from), to: iso(to) };
    }
    default:
      return { unsupported: 'Unknown date range.' };
  }
}

const short = (abs: number, coin = false) =>
  abs >= 1e6
    ? `${(abs / 1e6).toFixed(2)}M`
    : abs >= 1e3
      ? `${(abs / 1e3).toFixed(1)}K`
      : abs >= 1 || abs === 0 || !coin
        ? abs.toFixed(2)
        : String(Number(abs.toPrecision(3))); // small coin amounts, e.g. 0.0012 BTC

/** The mock's time format: "45s", "12m 05s", "3.2 hours", "4.1 days". */
export function humanSeconds(sec: number): string {
  if (sec < 90) {
    return `${Math.round(sec)}s`;
  }
  if (sec < 5400) {
    const t = Math.round(sec);
    return `${Math.floor(t / 60)}m ${String(t % 60).padStart(2, '0')}s`;
  }
  return sec < 172_800 ? `${(sec / 3600).toFixed(1)} hours` : `${(sec / 86_400).toFixed(1)} days`;
}

export function formatValue(r: Pick<MetricResult, 'value' | 'currency'> & { unit: string }): string {
  if (r.value === null) {
    return UNAVAILABLE;
  }
  const n = Number(r.value);
  if (!Number.isFinite(n)) {
    return UNAVAILABLE;
  }
  const sign = n < 0 ? '-' : '';
  if (r.unit === 'seconds') {
    return humanSeconds(n);
  }
  if (r.unit === 'count') {
    return n.toLocaleString('en-US');
  }
  if (r.unit === 'ratio') {
    return `${(n * 100).toFixed(1)}%`;
  }
  if (r.unit === 'amount') {
    return `${sign}${short(Math.abs(n), true)} ${(r.currency ?? '').toUpperCase()}`;
  }
  return `${sign}$${short(Math.abs(n))}`;
}

const COMPARE_OPEN = '● Comparison not available yet (Q22)';

// The line under a card that has a number: how far to trust it. Its "?" shows the details.
const TRUST: Record<string, { line: string; notes: string }> = {
  ready: { line: '● Management’s rule · no comparison yet', notes: 'Notes' },
  partial: { line: '● Uses default rules', notes: 'Defaults and notes' },
  observed: { line: '● Observed, not yet qualified', notes: 'Not yet qualified' },
};

type Explained = Pick<MetricResult, 'status' | 'basis' | 'missingInputs'>;

function tooltip(r: Explained): string {
  const notes = r.missingInputs.map((m) => `• ${m.description}`).join('\n');
  return [r.basis ?? '', notes && `${TRUST[r.status]?.notes ?? 'Notes'}:\n${notes}`].filter(Boolean).join('\n\n');
}

/** Fills a filter's options from /capabilities, keeping the mock's "All" first and the current pick if
 *  still offered. True when the pick had to change (it is no longer offered). */
function fillSelect(el: HTMLSelectElement | null, values: string[], label = (v: string) => v): boolean {
  if (!el) {
    return false;
  }
  const keep = el.value;
  el.replaceChildren(new Option('All', '*'), ...values.map((v) => new Option(label(v), v)));
  el.value = values.includes(keep) ? keep : '*';
  return el.value !== keep;
}

/** The data set's filter values into the CEO, Funnel and CRM panes' dropdowns. Says which pane's picks
 *  changed: that pane must load again, or it would show the old pick's numbers under the new one. */
export function setFilterOptions(filters: CapabilitiesResponse['filters']): {
  ceo: boolean;
  funnel: boolean;
  crm: boolean;
} {
  const $ = (id: string) => document.getElementById(id) as HTMLSelectElement | null;
  const currency = fillSelect($('ceo-f-cur'), filters.currency, (c) => c.toUpperCase());
  // Games by id (a renamed game is still one game), shown by label.
  const labels = new Map(filters.game.map((g) => [g.id, g.label]));
  const ids = [...labels.keys()];
  const label = (id: string) => labels.get(id) ?? id;
  const game = fillSelect($('ceo-f-game'), ids, label);
  // Favourite Product: the same games, and the players with none (the bridge's NO_FAVOURITE).
  const crm = fillSelect($('crm-f-game'), [...ids, 'none'], (id) => (id === 'none' ? 'No bet in 90 days' : label(id)));
  return { ceo: currency || game, funnel: fillSelect($('funnel-f-game'), ids, label), crm };
}

/** Why a figure has no number, for its "?". */
function reasonOf(r: Explained): string {
  const why = r.missingInputs.map((m) => m.description).join('\n') || r.status;
  return r.status === 'unavailable' || r.status === 'notApplicable' ? why : `${r.status}: ${why}`;
}

/** A card without a number. Nothing to divide (notApplicable) reads "—", as in the mock; anything
 *  else reads Unavailable. Either way the reason is behind its "?". */
function setNoNumber(el: HTMLElement, r: Explained) {
  if (r.status !== 'notApplicable') {
    setUnavailable(el, reasonOf(r));
    return;
  }
  el.textContent = '—';
  el.classList.remove('na');
  el.title = reasonOf(r);
}

/** "Data set: DS gold · Our gold", just above the sidebar's Data Source box. */
export function setDataSetSwitch(sets: CapabilitiesResponse['dataSets'], onChoose: (id: DataSetId) => void) {
  const note = document.querySelector('.side-note');
  if (!note || sets.length < 2) {
    return;
  }
  let el = document.querySelector<HTMLElement>('.data-set-switch');
  if (!el) {
    el = document.createElement('div');
    el.className = 'data-set-switch';
    // The mock pins the box to the sidebar's foot: the switch and the box share one pinned group.
    const foot = document.createElement('div');
    foot.className = 'side-foot';
    note.before(foot);
    foot.append(el, note);
  }
  el.replaceChildren('Data set: ');
  sets.forEach((s, i) => {
    if (i) {
      el.append(' · ');
    }
    const a = document.createElement('a');
    a.href = '#';
    a.textContent = s.label;
    a.dataset.set = s.id;
    if (s.id === dataSet) {
      a.setAttribute('aria-current', 'true');
    }
    a.addEventListener('click', (e) => {
      e.preventDefault();
      if (s.id !== dataSet) {
        onChoose(s.id);
      }
    });
    el.append(a);
  });
}

// ---- CEO pane panels: Revenue Trend, Player Funnel, Top Games (the mock's markup and chart settings) ----
const DAY = 86_400_000;
const TREND_MIN = 7; // the mock: a trend always spans at least a week ending where the range ends
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtLabel = (d: string) => `${MON[Number(d.slice(5, 7)) - 1]} ${Number(d.slice(8, 10))}`;
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/** The mock's usdK, for dollars. */
function usdK(n: number): string {
  const v = Math.abs(n);
  const sign = n < 0 ? '-$' : '$';
  if (v >= 1e6) {
    return `${sign}${(v / 1e6).toFixed(2)}M`;
  }
  if (v >= 1e4) {
    return `${sign}${(v / 1e3).toFixed(1)}K`;
  }
  return v >= 1e3
    ? `${sign}${(v / 1e3).toFixed(2)}K`
    : `${sign}${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
const pctS = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : '—');
const reasons = (m: { description: string }[]) => m.map((x) => x.description).join('\n');

/** The Revenue Trend's range: the selected one, or the week ending where it ends if shorter. */
export function trendRange(r: { from?: string; to: string }): { from?: string; to: string } {
  if (r.from === undefined || Date.parse(r.to) - Date.parse(r.from) >= TREND_MIN * DAY) {
    return r;
  }
  return { from: new Date(Date.parse(r.to) - TREND_MIN * DAY).toISOString().replace('.000Z', 'Z'), to: r.to };
}

/** Days into the mock's buckets: daily, or 7-day totals past ~2 months. A bucket with an unknown day is unknown. */
export function bucketed(days: string[], values: (string | null)[]): { labels: string[]; data: (number | null)[] } {
  const size = days.length > 62 ? 7 : 1;
  const labels: string[] = [];
  const data: (number | null)[] = [];
  for (let i = 0; i < days.length; i += size) {
    // By day index: a value the series lacks (null, or past its end) makes the bucket unknown, never 0.
    const part = days.slice(i, i + size).map((_, j) => values[i + j]);
    labels.push(fmtLabel(days[i] ?? ''));
    data.push(part.some((v) => v === null || v === undefined) ? null : part.reduce((a, v) => a + Number(v), 0));
  }
  return { labels, data };
}

let revenueChart: Chart | undefined;
const REV_SUB = 'Deposits, Gross Gaming Revenue (GGR) and Net Gaming Revenue (NGR)';

/** The three panels without numbers, for one reason (an unusable range, or while loading). */
function panelsUnavailable(reason: string) {
  const wrap = document.getElementById('ceo-revenueChart')?.parentElement;
  wrap?.classList.add('is-unavailable');
  wrap?.setAttribute('title', reason);
  const sub = document.getElementById('ceo-rev-sub');
  if (sub) {
    sub.textContent = REV_SUB;
    sub.title = '';
  }
  const note = `<div class="unavailable-note" title="${escapeAttr(reason)}">${UNAVAILABLE}</div>`;
  const funnel = document.getElementById('ceo-funnel');
  if (funnel) {
    funnel.innerHTML = note;
  }
  const games = document.getElementById('ceo-gameTable');
  if (games) {
    games.innerHTML = `<tr class="unavailable-row"><td colspan="4" title="${escapeAttr(reason)}">${UNAVAILABLE}</td></tr>`;
  }
}

function drawRevenue(t: CeoTrendsResponse | { error: string }, shortRange: boolean) {
  const canvas = document.getElementById('ceo-revenueChart') as HTMLCanvasElement | null;
  const wrap = canvas?.parentElement;
  const sub = document.getElementById('ceo-rev-sub');
  if (!canvas || !wrap) {
    return;
  }
  const Names = { deposits: 'Deposits', ggr: 'GGR', ngr: 'NGR' } as const;
  const series = 'error' in t ? undefined : t.series;
  const shown = series ? (['deposits', 'ggr', 'ngr'] as const).filter((k) => series[k].status !== 'unavailable') : [];
  if (sub) {
    sub.textContent = REV_SUB; // the previous answer's "· last 7 days" or "· 7-day buckets" doesn't carry over
    sub.title = '';
  }
  if (!series || !('days' in t) || !shown.length || !t.days.length) {
    revenueChart?.destroy();
    revenueChart = undefined;
    wrap.classList.add('is-unavailable');
    wrap.title =
      'error' in t
        ? `Could not load: ${t.error}`
        : t.days.length || !shown.length
          ? reasons(Object.values(t.series).flatMap((x) => x.missingInputs))
          : 'No days with data in this range.';
    return;
  }
  wrap.classList.remove('is-unavailable');
  const weekly = t.days.length > 62;
  const wk = weekly ? ' (weekly totals)' : '';
  const beads = Math.ceil(t.days.length / (weekly ? 7 : 1)) > 62 ? 0 : 2; // the mock: by points drawn
  // The mock's three lines, colours and options; a series without data keeps its legend entry.
  const Style = {
    deposits: { borderColor: '#6b8cff', backgroundColor: 'rgba(107,140,255,.12)', fill: true },
    ggr: { borderColor: '#2bd576' },
    ngr: { borderColor: '#a97aff' },
  } as const;
  let labels: string[] = [];
  const datasets = (['deposits', 'ggr', 'ngr'] as const).map((k) => {
    const s = series[k];
    const b = bucketed(t.days, s.status === 'unavailable' ? t.days.map(() => null) : s.values);
    labels = b.labels;
    return {
      label: `${Names[k]}${wk}${s.status === 'unavailable' ? ' · Unavailable' : ''}`,
      data: s.status === 'unavailable' ? [] : b.data,
      tension: 0.35,
      pointRadius: beads,
      pointHoverRadius: 4,
      ...Style[k],
    };
  });
  wrap.title = ''; // a drawn chart's points have Chart.js's own tooltips; the notes go on the subtitle
  const notes = (['deposits', 'ggr', 'ngr'] as const)
    .map(
      (k) =>
        `${Names[k]}: ${series[k].basis ?? ''}${series[k].missingInputs.length ? `\n• ${series[k].missingInputs.map((m) => m.description).join('\n• ')}` : ''}`,
    )
    .join('\n\n');
  if (sub) {
    sub.textContent = `${REV_SUB}${weekly ? ' · 7-day buckets' : ''}${shortRange ? ` · last ${TREND_MIN} days` : ''}`;
    sub.title = notes;
  }
  if (revenueChart) {
    revenueChart.data = { labels, datasets };
    revenueChart.update();
    return;
  }
  revenueChart = new Chart(canvas, {
    type: 'line',
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: '#aab4c4', usePointStyle: true } },
        tooltip: {
          callbacks: {
            label: (c) =>
              `${c.dataset.label}: $${Number(c.parsed.y).toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
          },
        },
      },
      scales: {
        x: { ticks: { color: '#8591a3' }, grid: { color: 'rgba(38,50,71,.35)' } },
        y: {
          ticks: { color: '#8591a3', callback: (v) => `$${Number(v).toLocaleString('en-US')}` },
          grid: { color: 'rgba(38,50,71,.35)' },
        },
      },
    },
  });
}

function drawGames(b: CeoBreakdownResponse | { error: string }) {
  const tbody = document.getElementById('ceo-gameTable');
  if (!tbody) {
    return;
  }
  if ('error' in b || b.status === 'unavailable') {
    const why = 'error' in b ? `Could not load: ${b.error}` : reasons(b.missingInputs);
    tbody.innerHTML = `<tr class="unavailable-row"><td colspan="4" title="${escapeAttr(why)}">${UNAVAILABLE}</td></tr>`;
    return;
  }
  const head = tbody.closest('table')?.tHead?.rows[0];
  if (head) {
    head.title = [
      b.basis ?? '',
      b.missingInputs.length
        ? `Defaults and notes:\n${b.missingInputs.map((m) => `• ${m.description}`).join('\n')}`
        : '',
    ]
      .filter(Boolean)
      .join('\n\n');
  }
  if (!b.rows.length) {
    tbody.innerHTML =
      '<tr><td colspan="4" style="color:var(--muted)">No rounds were played in this selection.</td></tr>';
    return;
  }
  // A null figure reads Unavailable (with its reason, if one came) — never 0.
  const cell = (known: boolean, html: string, why: string) =>
    known ? `<td>${html}</td>` : `<td class="unavailable" title="${escapeAttr(why || 'Unknown.')}">${UNAVAILABLE}</td>`;
  tbody.innerHTML = b.rows
    .map((g) => {
      const why = reasons(g.missingInputs);
      return `<tr><td><strong>${esc(g.game ?? '(no game name)')}</strong></td>${cell(
        g.players !== null,
        (g.players ?? 0).toLocaleString('en-US'),
        why,
      )}${cell(g.ggr !== null, usdK(Number(g.ggr)), why)}${cell(
        g.players !== null && g.repeatPlayers !== null,
        `<span class="tag">${pctS(g.repeatPlayers ?? 0, g.players ?? 0)}</span>`,
        why,
      )}</tr>`;
    })
    .join('');
}

/** CEO pane: the twelve cards, Revenue Trend, Player Funnel and Top Games come from the bridge; the other panels are Unavailable. */
export function ceoPane(onSnapshot: (s: Snapshot) => void) {
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(`ceo-${id}`) as T | null;
  let seq = 0;

  const allCards = (reason: string) => {
    for (let i = 0; i < 12; i++) {
      const el = $(`k-${i}`);
      if (el) {
        setUnavailable(el, reason);
      }
    }
  };

  let shown: string | null | undefined; // the snapshot the cards show
  let retry = false; // something failed that may pass on its own: the next refresh loads again
  // The mock's D7 Retention line describes the mock's rule (÷ First-Time Depositors): with a number,
  // the card says what it counted instead (Q11 proposal), and the mock's line comes back otherwise.
  const d7 = CEO_METRIC_IDS.indexOf('day7PlayerRetention');
  let d7Mock: string | undefined;
  const d7Line = (r?: MetricResult) => {
    const sub = $(`s-${d7}`);
    if (!sub) {
      return;
    }
    d7Mock ??= sub.textContent ?? '';
    sub.textContent =
      r && r.value !== null && r.numerator !== null && r.denominator !== null
        ? `${r.numerator} of ${r.denominator} players from this range bet on their day 7 (the 24 hours from 7 days after sign-up).`
        : d7Mock;
  };

  // Each panel answers on its own: one that fails (e.g. a filter its data set can't apply) never
  // blanks the cards or the other panels.
  async function loadPanels(mine: number, q: CeoQuery) {
    const failed = (err: unknown) => {
      if (err instanceof Unauthenticated) {
        location.reload();
      }
      if (mine === seq && mayPass(err)) {
        retry = true;
      }
      return { error: (err as Error).message };
    };
    const trend = trendRange(q);
    const short = trend.from !== q.from;
    await Promise.all([
      api
        .ceoTrends({ ...q, ...trend })
        .catch(failed)
        .then((t) => mine === seq && drawRevenue(t, short)),
      api
        .ceoBreakdowns({ ...q, by: 'game' })
        .catch(failed)
        .then((b) => mine === seq && drawGames(b)),
      // The mock's Player Funnel: the Funnel pane's stages; the CEO Game filter is its First Game.
      api
        .funnel({ dataSet: q.dataSet, from: q.from, to: q.to, game: q.game, currency: q.currency })
        .catch(failed)
        .then((f) => {
          const el = $('funnel');
          if (mine !== seq || !el) {
            return;
          }
          el.innerHTML =
            'error' in f
              ? `<div class="unavailable-note" title="${escapeAttr(`Could not load: ${f.error}`)}">${UNAVAILABLE}</div>`
              : funnelRows(f.stages);
        }),
    ]);
  }

  async function load() {
    // Every load is a new generation: anything older that answers late is dropped.
    const mine = ++seq;
    shown = undefined; // nothing shown until this load answers
    retry = false;
    // The panels' old answers go at once too (another data set's lines or rows must not linger).
    revenueChart?.destroy();
    revenueChart = undefined;
    const games = $('gameTable');
    if (games) {
      games.innerHTML = '';
      const head = games.closest('table')?.tHead?.rows[0];
      if (head) {
        head.title = '';
      }
    }
    markUnavailable('ceo', NOT_BUILT);
    panelsUnavailable('Loading…');
    d7Line();
    const preset = $<HTMLSelectElement>('f-date')?.value ?? 'today';
    // The mock shows the From/To pickers only for "Custom range…".
    const rangeRow = $('range-row');
    if (rangeRow) {
      rangeRow.hidden = preset !== 'custom';
    }
    const range = rangeFor(preset, new Date(), {
      from: $<HTMLInputElement>('f-from')?.value,
      to: $<HTMLInputElement>('f-to')?.value,
    });
    if ('unsupported' in range) {
      allCards(range.unsupported);
      panelsUnavailable(range.unsupported);
      return;
    }
    const pick = (id: string) => {
      const v = $<HTMLSelectElement>(id)?.value;
      return v && v !== '*' ? v : undefined;
    };
    const q: CeoQuery = { dataSet, ...range, currency: pick('f-cur'), game: pick('f-game') };
    void loadPanels(mine, q);
    try {
      const summary = await api.ceoSummary(q);
      if (mine !== seq) {
        return; // a newer filter change or data-set switch is already loading
      }
      shown = summary.snapshotId;
      onSnapshot(summary);
      // Q22 leaves comparisons open: say so on every card rather than show nothing.
      const comparing = $<HTMLSelectElement>('f-cmp')?.value !== 'none';
      d7Line(summary.results[d7]);
      summary.results.forEach((r, i) => {
        const el = $(`k-${i}`);
        if (!el) {
          return;
        }
        const delta = $(`d-${i}`);
        if (r.value !== null) {
          el.textContent = formatValue(r);
          el.classList.remove('na');
          el.title = tooltip(r);
          if (delta) {
            delta.className = 'delta flat';
            delta.textContent = comparing ? COMPARE_OPEN : (TRUST[r.status]?.line ?? '');
          }
        } else {
          setNoNumber(el, r);
        }
      });
    } catch (err) {
      if (err instanceof Unauthenticated) {
        location.reload(); // the session ended: the page starts again at sign-in
        return;
      }
      if (mine === seq) {
        retry ||= mayPass(err);
        allCards(`Could not load: ${(err as Error).message}`);
      }
    }
  }

  let started = false;
  return {
    init() {
      started = true;
      startOnLastThirtyDays($<HTMLSelectElement>('f-date'));
      // Comparisons aren't available yet (Q22), so start on None instead of the mock's
      // Previous Period; picking another option says so on the cards.
      const cmp = $<HTMLSelectElement>('f-cmp');
      if (cmp) {
        cmp.value = 'none';
      }
      $('f-game')?.setAttribute('title', GAME_NOTE);
      $('f-cur')?.setAttribute('title', CURRENCY_NOTE);
      for (const id of ['f-date', 'f-cmp', 'f-geo', 'f-src', 'f-game', 'f-cur', 'f-from', 'f-to']) {
        $(id)?.addEventListener('change', () => void load());
      }
      void load();
    },
    /** Loads again (another data set, or new filter options); before the pane is shown, init does it. */
    reload() {
      if (started) {
        void load();
      }
    },
    /** The snapshot the cards show; undefined before the first answer. */
    shownSnapshotId: () => shown,
    /** Part of the last load failed in a way that may pass (no answer, or a 5xx): load again. */
    loadFailed: () => retry,
  };
}

// Panes that set their own header pill: the funnel reads its own publication, and the CRM pane says
// when the figures it shows were current (it loads on its own, so they can be newer than the others').
const OWN_PILL = ':not([data-pane="funnel"]):not([data-pane="crm"])';

// The mock's stage names, in its order (FUNNEL_STAGE_IDS).
const STAGE_NAMES = [
  'Tracking Link Opened',
  'Registration Started',
  'Registration Completed',
  'First-Time Deposit',
  'First Wager',
  'Second Deposit',
  'Day-7 Active',
];
// Day-7 Active counts every sign-up in the range, not the second depositors above it (Q11 proposal),
// so its row has no conversion or drop-off.
const DAY7 = STAGE_NAMES.indexOf('Day-7 Active');
// A deposit needs no bet, so second depositors need not have wagered: Second Deposit is not a share of
// First Wager either, and its row has no conversion or drop-off; its "?" gives the share of depositors.
const SECOND = STAGE_NAMES.indexOf('Second Deposit');
const FTD = STAGE_NAMES.indexOf('First-Time Deposit');
// The mock's Game and Currency tooltips describe attribution rules nobody has built yet (Q16, Q18).
const GAME_NOTE =
  'Our gold: rounds count on the game they were played on (Active Players, GGR, Top Games). Figures with no game (registrations, deposits, withdrawals) show Unavailable under a Game pick until Q18 is decided. DS gold has no Game split.';
const CURRENCY_NOTE =
  "Our gold: every currency, in USD at stored values; there is no currency to pick yet (Q16). DS gold: its amounts are per source currency, so money cards need one currency picked; counts aren't split by currency.";

const COHORT_NOTE =
  'Players are grouped by the UTC day they registered; each later step counts what those players have done so far, up to the data time.';

/** The mock's funnel rows: count bar, conversion from the step above, drop-off. A stage without a
 *  number keeps its row and reads Unavailable; the steps around it then have no conversion. */
function funnelRows(stages: (Explained & Pick<FunnelFigure, 'value'>)[]): string {
  const counts = stages.map((st) => (st.value === null ? null : Number(st.value)));
  const drops = dropOffs(counts);
  const max = Math.max(1, ...counts.filter((c): c is number => c !== null));
  let prev: number | null = null;
  return stages
    .map((st, i) => {
      const name = `<div class="funnel-name">${STAGE_NAMES[i]}</div>`;
      const c = counts[i] ?? null;
      if (c === null) {
        prev = null;
        // Nothing to count yet (notApplicable) reads "—", as the cards do; anything else, Unavailable.
        const bar =
          st.status === 'notApplicable'
            ? '<div class="bar-wrap bar-none">—</div>'
            : '<div class="bar-wrap is-unavailable"></div>';
        return `<div class="funnel-row" title="${escapeAttr(reasonOf(st))}">${name}${bar}<div class="conv">—</div><div class="drop">—</div></div>`;
      }
      const conv = prev && i !== DAY7 && i !== SECOND ? `${((c / prev) * 100).toFixed(1)}%` : '—';
      // A step can exceed the one above it: no negative drop.
      const grew = prev !== null && c > prev;
      const d = drops[i];
      const drop = d == null ? '—' : `${d.toFixed(1)}%`;
      const tip = [
        tooltip(st),
        i === DAY7
          ? 'Counted over every player who registered in the range, not only the step above, so it has no conversion or drop-off.'
          : i === SECOND
            ? `Second depositors need not have wagered, so this row has no conversion or drop-off from First Wager.${secondShare(c, counts[FTD])}`
            : grew
              ? 'Not a subset of the step above.'
              : '',
      ]
        .filter(Boolean)
        .join('\n\n');
      prev = c;
      return `<div class="funnel-row" title="${escapeAttr(tip)}">${name}<div class="bar-wrap"><div class="bar" style="width:${Math.max(9, (c / max) * 100)}%">${c.toLocaleString('en-US')}</div></div><div class="conv">${conv}</div><div class="drop">${drop}</div></div>`;
    })
    .join('');
}

const secondShare = (second: number, ftd: number | null | undefined) =>
  ftd
    ? ` ${second.toLocaleString('en-US')} of the ${ftd.toLocaleString('en-US')} First-Time Depositors above (${((second / ftd) * 100).toFixed(1)}%) have made a second deposit.`
    : '';

/** Each stage's drop-off from the step above, as the funnel rows show it: none for the first stage,
 *  Second Deposit and Day-7 Active (not shares of the step above), after an Unavailable or empty
 *  step, or when the step grew (no negative drop). */
const dropOffs = (counts: (number | null)[]) =>
  counts.map((c, i) => {
    const prev = counts[i - 1];
    return c === null || !prev || c > prev || i === DAY7 || i === SECOND ? null : 100 - (c / prev) * 100;
  });

const MOCK_RULE = 'The mock’s rule; management has not confirmed it.';

/** Funnel Actions: the mock's cards (its funnel report) from the stage counts, so both data sets get
 *  them. The mock's words are kept where they hold on real data: "largest leak" only when it is, and
 *  no "shortly after funding" (no time limit is applied). Its declined-payments and mobile sign-up
 *  cards can't be built yet: one note after the cards gives the bridge's reasons. */
function funnelActions(stages: (Explained & Pick<FunnelFigure, 'value'>)[], missingInputs: MissingInput[]): string {
  // The cards read Registration Completed, First-Time Deposit and First Wager: without one of them the
  // whole panel is Unavailable with its reasons, rather than some cards silently missing.
  const unknown = stages.slice(2, 5).filter((st) => st.value === null);
  if (unknown.length) {
    const reasons = [...new Map(unknown.flatMap((st) => st.missingInputs).map((m) => [m.description, m])).values()];
    return `<div class="unavailable-note" title="${escapeAttr(reasonOf({ status: 'unavailable', basis: null, missingInputs: reasons }))}">${UNAVAILABLE}</div>`;
  }
  const counts = stages.map((st) => (st.value === null ? null : Number(st.value)));
  const [, , registered, deposited, wagered] = counts;
  const drops = dropOffs(counts);
  const leak = drops[3];
  const cards: [level: string, title: string, text: string, rule: string][] = [];
  if (registered === 0) {
    cards.push([
      'amber',
      'No registrations in this selection',
      'No player registered in these dates and filters — widen the date range or clear a filter.',
      `${MOCK_RULE} Shown when no player registered in the selection.`,
    ]);
  }
  if (registered && leak != null && leak > 0 && drops.every((d) => d == null || d <= leak)) {
    cards.push([
      'red',
      'Deposit step is the largest leak',
      `${leak.toFixed(1)}% of registered players have not made their first deposit.`,
      `${MOCK_RULE} Shown when the step to First-Time Deposit loses the largest share of players of the steps with a drop-off.`,
    ]);
  }
  if (deposited && wagered != null) {
    cards.push([
      'green',
      'First wager flow is healthy',
      `${((wagered / deposited) * 100).toFixed(1)}% of First-Time Depositors have wagered since their first deposit.`,
      `${MOCK_RULE} Shown whenever a player from the selection has deposited: no healthy level is set, so the colour and the word are the mock’s.`,
    ]);
  }
  const n = missingInputs.length;
  const note = n
    ? `<div class="unavailable-note" title="${escapeAttr(reasonOf({ status: 'unavailable', basis: null, missingInputs }))}">${n === 1 ? '1 more card' : `${n} more cards`}: ${UNAVAILABLE}</div>`
    : '';
  return (
    cards
      .map(
        ([level, title, text, rule]) =>
          `<div class="alert" title="${escapeAttr(rule)}"><span class="alert-icon ${level}"></span><div><strong>${title}</strong><p>${text}</p></div></div>`,
      )
      .join('') + note
  );
}

/** Funnel pane: cards, stages, timings and Funnel Actions from the bridge's registration cohort.
 *  Drop-Off by Source stays Unavailable, with the bridge's reason. */
export function funnelPane() {
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(`funnel-${id}`) as T | null;
  const cardKeys = ['opens', 'reg', 'ftd', 'sec'];
  const subs: Record<string, string> = {};
  let seq = 0;

  const blank = (reason: string) => {
    for (const k of cardKeys) {
      const el = $(`k-${k}`);
      if (el) {
        setUnavailable(el, reason);
      }
      const d = $(`d-${k}`);
      if (d) {
        d.className = 'delta flat';
        d.textContent = '';
      }
      const sub = $(`s-${k}`);
      if (sub && subs[k] !== undefined) {
        sub.textContent = subs[k];
      }
    }
    const rows = $('mainFunnel');
    if (rows) {
      const none = {
        status: 'unavailable' as const,
        basis: null,
        value: null,
        missingInputs: [{ code: 'x', description: reason }],
      };
      rows.innerHTML = funnelRows(STAGE_NAMES.map(() => none));
    }
    for (const row of $<HTMLTableSectionElement>('timing-tb')?.rows ?? []) {
      const [, avg, change] = row.cells;
      if (avg && change) {
        avg.textContent = UNAVAILABLE;
        avg.className = 'unavailable';
        avg.title = reason;
        change.textContent = '—';
        change.className = 'flat';
        change.title = 'Comparison not available yet (Q22).';
      }
    }
    const chart = $('dropChart')?.parentElement;
    chart?.classList.add('is-unavailable');
    chart?.setAttribute('title', reason);
    const alerts = $('alerts');
    if (alerts) {
      alerts.innerHTML = `<div class="unavailable-note" title="${escapeAttr(reason)}">${UNAVAILABLE}</div>`;
    }
  };

  // quiet: a periodic refresh keeps the numbers on screen until the new ones arrive.
  async function load(quiet = false) {
    const mine = ++seq;
    if (!quiet) {
      blank('Loading…');
    }
    const preset = $<HTMLSelectElement>('f-date')?.value ?? 'today';
    const rangeRow = $('range-row');
    if (rangeRow) {
      rangeRow.hidden = preset !== 'custom';
    }
    const range = rangeFor(preset, new Date(), {
      from: $<HTMLInputElement>('f-from')?.value,
      to: $<HTMLInputElement>('f-to')?.value,
    });
    if ('unsupported' in range) {
      blank(range.unsupported);
      return;
    }
    const pick = (id: string) => {
      const v = $<HTMLSelectElement>(id)?.value;
      return v && v !== '*' ? v : undefined;
    };
    try {
      const f = await api.funnel({
        dataSet,
        ...range,
        game: pick('f-game'),
        device: pick('f-dev') as 'desktop' | 'mobile' | undefined,
      });
      if (mine !== seq) {
        return; // a newer filter change or data-set switch is already loading
      }
      setStatusPills(f, pane('funnel') ?? undefined);
      f.cards.forEach((c, i) => {
        const k = cardKeys[i] ?? '';
        const el = $(`k-${k}`);
        const d = $(`d-${k}`);
        const sub = $(`s-${k}`);
        // Every line of the card is rewritten: a quiet refresh must not leave the old counts under a new value.
        if (d) {
          d.className = 'delta flat';
          d.textContent = c.value === null ? '' : (TRUST[c.status]?.line ?? '');
        }
        if (sub) {
          sub.textContent =
            c.value === null || c.numerator === null || c.denominator === null
              ? (subs[k] ?? '')
              : c.id === 'ftdConversion'
                ? `${c.numerator} of ${c.denominator} players who registered in this range have deposited.`
                : `${c.numerator} of ${c.denominator} depositors from this range have made a second deposit.`;
        }
        if (!el) {
          return;
        }
        if (c.value === null) {
          setNoNumber(el, c);
          return;
        }
        el.textContent = formatValue({ ...c, currency: null });
        el.classList.remove('na');
        el.title = tooltip(c);
      });
      const rows = $('mainFunnel');
      if (rows) {
        rows.innerHTML = funnelRows(f.stages);
      }
      f.gaps.forEach((g, i) => {
        const avg = $<HTMLTableSectionElement>('timing-tb')?.rows[i]?.cells[1];
        if (!avg) {
          return;
        }
        if (g.value === null) {
          avg.textContent = g.status === 'notApplicable' ? '—' : UNAVAILABLE;
          avg.className = 'unavailable';
          avg.title = reasonOf(g);
          return;
        }
        avg.textContent = formatValue({ ...g, currency: null });
        avg.className = '';
        avg.title = [`${g.denominator ?? 0} player${g.denominator === 1 ? '' : 's'}`, tooltip(g)].join(' · ');
      });
      $('dropChart')?.parentElement?.setAttribute(
        'title',
        reasonOf({ status: 'unavailable', basis: null, missingInputs: f.unavailable.dropOffBySource }),
      );
      const alerts = $('alerts');
      if (alerts) {
        alerts.innerHTML = funnelActions(f.stages, f.unavailable.actions);
      }
    } catch (err) {
      if (err instanceof Unauthenticated) {
        location.reload();
        return;
      }
      if (mine === seq) {
        blank(`Could not load: ${(err as Error).message}`);
        setStatusPills(undefined, pane('funnel') ?? undefined);
      }
    }
  }

  let started = false;
  return {
    init() {
      started = true;
      for (const k of cardKeys) {
        subs[k] = $(`s-${k}`)?.textContent ?? '';
      }
      startOnLastThirtyDays($<HTMLSelectElement>('f-date'));
      // The mock's tooltip described another time basis; say what these numbers count.
      $('f-date')?.setAttribute('title', COHORT_NOTE);
      for (const id of ['f-date', 'f-from', 'f-to', 'f-src', 'f-camp', 'f-geo', 'f-dev', 'f-game']) {
        $(id)?.addEventListener('change', () => void load());
      }
      void load();
    },
    /** Loads again: at once blank for another data set, quietly for a periodic refresh. */
    reload(quiet = false) {
      if (started) {
        void load(quiet);
      }
    },
  };
}

// ---- CRM pane: four figures from the CEO summary (as on DS's own CRM page), the Retention Cohort
// Trend and player health from /crm, and every other panel with the bridge's reason (CEO sheet
// Q23-Q28). ----
const CRM_FIGURES = [
  ['k-0', 'day7PlayerRetention'],
  ['k-1', 'secondDepositRate'],
  ['n-0', 'newRegistrations'],
  ['n-1', 'activePlayers'],
] as const;
// The elements of each panel the bridge has no data for.
const CRM_PANELS: Record<CrmPanelId, string[]> = {
  highChurnRisk: ['k-2'],
  reactivation: ['k-3'],
  atRisk: ['n-2'],
  churned: ['n-3'],
  baseline: ['n-4', 'n-5', 'n-6', 'n-7'],
  riskBreakdown: ['riskBreakdown'],
  exampleDeviation: ['tb-0'],
  churnSignals: ['churnReasons'],
  attention: ['tb-1'],
  campaigns: ['tb-2'],
  actions: ['al-0'],
};
const DEFINITIONS_NOTE =
  'The mock’s proposed rules, not yet decided by management (CEO sheet Q23). Our gold applies them, with thresholds of its own, until management confirms; DS gold does not yet.';
let retentionChart: Chart | undefined;
// The mock's decline signals, with its labels and colours.
const SIGNALS: Record<CrmHealth['signals'][number]['id'], [label: string, colour: string]> = {
  wagerVolume: ['Wager volume down', '#ff6173'],
  bettingFrequency: ['Betting frequency down', '#ffbd4a'],
  depositAmount: ['Deposit amount down', '#6b8cff'],
  depositFrequency: ['Deposit frequency down', '#a97aff'],
  noRecentWagers: ['No recent wagers', '#40d9d0'],
};
const PROPOSED = ' · proposed rules until management confirms (Q23)';
const SEGMENT_NOTE =
  'A proposal until management decides (Q28). New: signed up in the selected dates. Active: placed a real-money bet in the selected dates. At Risk: At Risk or High Churn Risk, and Churned: Churned, by the player-health proposal (Q23) at the data time. Every figure on this pane then counts only those players. VIP waits on how player levels map to tiers (Q26). DS gold has no Segment filter.';
const FAVOURITE_NOTE =
  "A proposal until management decides (Q27): a player's favourite game is the one they bet the most USD on in real-money bets over the 90 days before the data time (a tie goes to more bets, then the game's id). Every figure on this pane then counts only those players. DS gold has no Favourite Product filter.";
const ACTIONS_NOTE =
  "A proposal until management decides (Q28): red for players at High Churn Risk (3 or more decline signals, Q23); amber for players with no real-money bet for 30 days or more; Day-7 retention green at 30% or more (the mock's line), amber below it. The mock's campaign conversion and worst-case player are left out until there are campaign records (Q24) and a decision on listing players (Q25).";
const PROPOSED_ACTIONS = ' · proposed rules until management confirms (Q28)';
const DAY7_LINE = 0.3; // the mock's: Day-7 retention at or above it is green

export function crmPane() {
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(`crm-${id}`) as T | null;
  const subs: Record<string, string> = {};
  let seq = 0;

  /** One element without data, in its own slot: a card or segment number, a table, a chart or the list. */
  const none = (id: string, reason: string) => {
    const el = $(id);
    if (el instanceof HTMLTableSectionElement) {
      const cols = el.closest('table')?.querySelectorAll('thead th').length ?? 1;
      el.innerHTML = `<tr class="unavailable-row"><td colspan="${cols}" title="${escapeAttr(reason)}">${UNAVAILABLE}</td></tr>`;
    } else if (el instanceof HTMLCanvasElement) {
      if (el.id !== 'crm-retentionTrend') {
        Chart.getChart(el)?.destroy(); // the trend's chart is noTrend's
      }
      el.parentElement?.classList.add('is-unavailable');
      el.parentElement?.setAttribute('title', reason);
    } else if (el?.classList.contains('alert-list')) {
      el.innerHTML = `<div class="unavailable-note" title="${escapeAttr(reason)}">${UNAVAILABLE}</div>`;
    } else if (el) {
      setUnavailable(el, reason);
    }
  };
  const sectionHead = (id: string) =>
    $(id)?.closest('.section')?.querySelector<HTMLElement>('.section-head span') ?? undefined;
  /** Without health (another data set, a failed or blank load), the High Churn-Risk card's lines, the
   *  baseline's subtitle and the charts' subtitles go back to the mock's, without the rule. */
  const noHealth = () => {
    const sub = $('s-2');
    if (sub) {
      sub.textContent = subs['s-2'] ?? '';
    }
    const delta = $('d-2');
    if (delta) {
      delta.className = 'delta flat';
      delta.textContent = '● comparison unavailable';
      delta.title = '';
    }
    const baseline = sectionHead('n-4');
    if (baseline) {
      baseline.textContent = subs.baseline ?? '';
      baseline.title = '';
    }
    for (const id of ['riskBreakdown', 'churnReasons']) {
      sectionHead(id)?.setAttribute('title', '');
    }
    // CRM Actions come from health: without it, its subtitle goes back to the mock's too.
    const actions = sectionHead('al-0');
    if (actions) {
      actions.textContent = subs.actions ?? '';
      actions.title = '';
    }
  };
  const trendHead = () => $('retentionTrend')?.closest('.section')?.querySelector<HTMLElement>('.section-head span');
  const noTrend = (reason: string) => {
    retentionChart?.destroy();
    retentionChart = undefined;
    none('retentionTrend', reason);
    trendHead()?.setAttribute('title', '');
  };
  const figuresNone = (reason: string) => {
    for (const [id] of CRM_FIGURES) {
      none(id, reason);
    }
    for (const k of ['s-0', 's-1', 's-2']) {
      const sub = $(k);
      if (sub) {
        sub.textContent = subs[k] ?? '';
      }
    }
    for (let i = 0; i < 4; i++) {
      const d = $(`d-${i}`);
      if (d) {
        d.className = 'delta flat';
        d.textContent = '● comparison unavailable';
      }
    }
  };
  // No figures shown (a refused pick, an unusable range): no data time either, not the last one's.
  const noPill = () => {
    for (const el of pane('crm')?.querySelectorAll<HTMLElement>('.status-pill') ?? []) {
      el.textContent = '● Data through —';
      el.classList.remove('stale');
    }
  };
  const blank = (reason: string) => {
    figuresNone(reason);
    for (const ids of Object.values(CRM_PANELS)) {
      for (const id of ids) {
        none(id, reason);
      }
    }
    noHealth();
    noTrend(reason);
  };

  function drawFigures(results: MetricResult[]) {
    for (const [id, metric] of CRM_FIGURES) {
      const el = $(id);
      const r = results.find((x) => x.metricId === metric);
      // Every line of a card is rewritten: a quiet refresh must not leave the old counts under a new value.
      const card = id.startsWith('k-') ? id.slice(2) : undefined;
      const d = card ? $(`d-${card}`) : null;
      if (d) {
        d.className = 'delta flat';
        d.textContent = r && r.value !== null ? (TRUST[r.status]?.line ?? '') : '● comparison unavailable';
      }
      const sub = card ? $(`s-${card}`) : null;
      if (sub) {
        sub.textContent =
          r && r.value !== null && r.numerator !== null && r.denominator !== null
            ? metric === 'day7PlayerRetention'
              ? `${r.numerator} of ${r.denominator} players from this range bet on their day 7 (the 24 hours from 7 days after sign-up).`
              : `${r.numerator} of ${r.denominator} first-time depositors from this range have made a second deposit.`
            : (subs[`s-${card}`] ?? '');
      }
      if (!el || !r) {
        none(id, 'The summary has no such figure.');
        continue;
      }
      if (r.value === null) {
        setNoNumber(el, r);
        continue;
      }
      el.textContent = formatValue(r);
      el.classList.remove('na');
      el.title = tooltip(r);
    }
  }

  /** The health panels, by the bridge's rule: the High Churn-Risk card, the At-Risk and Churned
   *  counts, the rule's windows and both charts. Each says behind its "?" that the rule is a proposal. Without
   *  health, load has already marked them Unavailable; the rest is noHealth's. */
  function drawHealth(h: CrmHealth) {
    const sub = $('s-2');
    const delta = $('d-2');
    const baseline = sectionHead('n-4');
    const { players: p, rule } = h;
    if (h.status === 'unavailable' || !p || !rule) {
      noHealth();
      return;
    }
    const tip = tooltip(h);
    const n = (v: number) => v.toLocaleString('en-US');
    const show = (id: string, text: string) => {
      const el = $(id);
      if (el) {
        el.textContent = text;
        el.classList.remove('na');
        el.title = tip;
      }
    };
    show('k-2', n(p.highChurnRisk));
    if (sub) {
      sub.textContent = `Three or more decline signals against their own ${rule.baselineDays}-day baseline (${n(p.atRisk)} more at two signals).`;
    }
    if (delta) {
      delta.className = 'delta flat';
      delta.textContent = TRUST[h.status]?.line ?? ''; // the number's "?" has the details
    }
    show('n-2', n(p.atRisk));
    show('n-3', n(p.churned));
    show('n-4', `${rule.baselineDays} Days`);
    show('n-5', `${rule.wagerDays} Days`);
    show('n-6', `${rule.depositDays} Days`);
    show('n-7', `${rule.flagSignals}+ Signals`);
    const flag = $('n-7');
    if (flag) {
      flag.title = `A player is flagged from ${rule.flagSignals} decline signals: ${rule.flagSignals} make them At Risk, 3 or more High Churn Risk (no real-money bet for ${rule.churnDays} days is Churned). The breakdown and signal charts count every flagged player.\n\n${tip}`;
    }
    if (baseline) {
      const remark = document.createElement('span');
      remark.className = 'flat';
      remark.textContent = PROPOSED;
      baseline.replaceChildren(subs.baseline ?? '', remark);
      baseline.title = tip;
    }
    const chart = (id: string, make: (canvas: HTMLCanvasElement) => Chart, data: (c: Chart) => void) => {
      const canvas = $<HTMLCanvasElement>(id);
      if (!canvas) {
        return;
      }
      canvas.parentElement?.classList.remove('is-unavailable');
      canvas.parentElement?.setAttribute('title', ''); // the bars have Chart.js's tooltips; the rule is on the subtitle
      sectionHead(id)?.setAttribute('title', tip);
      const c = Chart.getChart(canvas);
      if (c) {
        data(c);
        c.update();
      } else {
        make(canvas);
      }
    };
    const ids = h.signals.map((x) => x.id);
    const triggered = h.signals.map((x) => x.triggered);
    chart(
      'riskBreakdown',
      (canvas) =>
        new Chart(canvas, {
          type: 'bar',
          data: {
            labels: ids.map((id) => SIGNALS[id][0]),
            datasets: [
              { label: 'Players Triggered', data: triggered, backgroundColor: ids.map((id) => SIGNALS[id][1]) },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { labels: { color: '#aab4c4' } } },
            scales: {
              x: { ticks: { color: '#8591a3' }, grid: { color: 'rgba(38,50,71,.3)' } },
              y: { beginAtZero: true, ticks: { color: '#8591a3' }, grid: { color: 'rgba(38,50,71,.3)' } },
            },
          },
        }),
      (c) => {
        const set = c.data.datasets[0];
        if (set) {
          set.data = triggered;
        }
      },
    );
    // As in the mock, only the reasons someone has.
    const main = h.signals.filter((x) => x.main > 0);
    chart(
      'churnReasons',
      (canvas) =>
        new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: main.map((x) => SIGNALS[x.id][0]),
            datasets: [
              { data: main.map((x) => x.main), backgroundColor: main.map((x) => SIGNALS[x.id][1]), borderWidth: 0 },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { position: 'right', labels: { color: '#aab4c4', usePointStyle: true } } },
          },
        }),
      (c) => {
        c.data.labels = main.map((x) => SIGNALS[x.id][0]);
        const set = c.data.datasets[0];
        if (set) {
          set.data = main.map((x) => x.main);
          set.backgroundColor = main.map((x) => SIGNALS[x.id][1]);
        }
      },
    );
  }

  /** The mock's CRM Actions, by the Q28 proposal, from health and the Day-7 card: players at high churn
   *  risk, players dormant 30+ days, and Day-7 retention against the mock's 30% line. The mock's campaign
   *  line and worst-case player are left out (Q24, Q25). Without health, load has marked it Unavailable. */
  function drawActions(h: CrmHealth, results: MetricResult[] | undefined) {
    const list = $('al-0');
    const { players: p, rule } = h;
    if (!list || h.status === 'unavailable' || !p || !rule || h.dormant === null) {
      return;
    }
    const players = (v: number | string) => `${Number(v).toLocaleString('en-US')} player${Number(v) === 1 ? '' : 's'}`;
    const alerts: [colour: string, title: string, text: string][] = [];
    if (p.highChurnRisk) {
      alerts.push([
        'red',
        `${players(p.highChurnRisk)} at high churn risk`,
        `Three or more decline signals against their own ${rule.baselineDays}-day baseline.`,
      ]);
    }
    if (h.dormant) {
      alerts.push([
        'amber',
        `${players(h.dormant)} dormant ${rule.dormantDays}+ days`,
        `The win-back pool: no real-money bet for ${rule.dormantDays} days or more.`,
      ]);
    }
    const d7 = results?.find((r) => r.metricId === 'day7PlayerRetention');
    if (d7?.value != null && d7.denominator) {
      alerts.push([
        Number(d7.value) >= DAY7_LINE ? 'green' : 'amber',
        `Day-7 retention ${formatValue(d7)}`,
        `Measured over ${players(d7.denominator)} old enough to have reached day 7; younger players are left out rather than counted as churned.`,
      ]);
    }
    if (!Object.values(p).some(Boolean)) {
      alerts.push(['amber', 'No players match these filters', 'Widen the date range or clear a filter.']);
    }
    list.replaceChildren(
      ...alerts.map(([colour, title, text]) => {
        const alert = document.createElement('div');
        alert.className = 'alert';
        const icon = document.createElement('span');
        icon.className = `alert-icon ${colour}`;
        const body = document.createElement('div');
        const strong = document.createElement('strong');
        strong.textContent = title;
        const line = document.createElement('p');
        line.textContent = text;
        body.append(strong, line);
        alert.append(icon, body);
        return alert;
      }),
    );
    // What couldn't be checked is said, and then there is never an all-clear: a Day-7 card that failed or
    // is Unavailable, and health with a data gap (any note but a stated default rule: rounds that can't
    // be rebuilt, amounts with no USD value).
    const unchecked = [
      ...(d7 !== undefined && (d7.value !== null || d7.status === 'notApplicable')
        ? []
        : ['Day-7 retention is unavailable, so its follow-up can’t be checked.']),
      ...(h.missingInputs.some((m) => m.code !== 'default_rule')
        ? [
            'Player health has gaps in its data (the “?” on High Churn-Risk Players says which), so a follow-up may be missing.',
          ]
        : []),
    ];
    for (const text of alerts.length || unchecked.length ? unchecked : ['Nothing needs follow-up under these rules.']) {
      const quiet = document.createElement('div');
      quiet.className = 'unavailable-note';
      quiet.textContent = text;
      list.append(quiet);
    }
    const head = sectionHead('al-0');
    if (head) {
      const remark = document.createElement('span');
      remark.className = 'flat';
      remark.textContent = PROPOSED_ACTIONS;
      head.replaceChildren(subs.actions ?? '', remark);
      head.title = ACTIONS_NOTE;
    }
  }

  let cohortsShown: CrmCohort[] = []; // the drawn sign-up days, for the points' tooltips
  /** The mock's Retention Cohort Trend: a line per day (1, 7, 30) over the sign-up days. A day with no
   *  sign-ups, or whose players haven't reached that day yet, is a gap, never 0. */
  function drawRetention(c: CrmResponse) {
    const canvas = $<HTMLCanvasElement>('retentionTrend');
    const { status, cohorts } = c.retention;
    if (!canvas || status === 'unavailable' || !cohorts.some((g) => g.registrations)) {
      noTrend(status === 'unavailable' ? reasonOf(c.retention) : 'No players registered in this range.');
      return;
    }
    canvas.parentElement?.classList.remove('is-unavailable');
    canvas.parentElement?.setAttribute('title', ''); // the points have Chart.js's tooltips; the notes go on the subtitle
    trendHead()?.setAttribute('title', tooltip(c.retention));
    cohortsShown = cohorts;
    const keys = ['day1', 'day7', 'day30'] as const;
    const labels = cohorts.map((g) => fmtLabel(g.from));
    const datasets = (
      [
        ['Day 1', '#6b8cff'],
        ['Day 7', '#2bd576'],
        ['Day 30', '#a97aff'],
      ] as const
    ).map(([label, borderColor], k) => ({
      label,
      borderColor,
      tension: 0.35,
      data: cohorts.map((g) => {
        const v = g[keys[k] ?? 'day1'].value;
        return v === null ? null : Number((Number(v) * 100).toFixed(1));
      }),
    }));
    if (retentionChart) {
      retentionChart.data = { labels, datasets };
      retentionChart.update();
      return;
    }
    retentionChart = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { labels: { color: '#aab4c4', usePointStyle: true } },
          tooltip: {
            callbacks: {
              label: (t) => {
                const c = cohortsShown[t.dataIndex];
                const g = c?.[keys[t.datasetIndex] ?? 'day1'];
                const v =
                  t.parsed.y !== null ? `${t.parsed.y}%` : c?.registrations ? 'no mature cohort' : 'no sign-ups';
                return `${t.dataset.label}: ${v}${g ? ` (${g.retained} of ${g.eligible})` : ''}`;
              },
            },
          },
        },
        scales: {
          x: { ticks: { color: '#8591a3' }, grid: { color: 'rgba(38,50,71,.3)' } },
          y: {
            beginAtZero: true,
            ticks: { color: '#8591a3', callback: (v) => `${v}%` },
            grid: { color: 'rgba(38,50,71,.3)' },
          },
        },
      },
    });
  }
  // quiet: a periodic refresh keeps the numbers on screen until the new ones arrive.
  async function load(quiet = false) {
    const mine = ++seq;
    if (!quiet) {
      blank('Loading…');
    }
    const preset = $<HTMLSelectElement>('f-date')?.value ?? 'today';
    const rangeRow = $('range-row');
    if (rangeRow) {
      rangeRow.hidden = preset !== 'custom';
    }
    const range = rangeFor(preset, new Date(), {
      from: $<HTMLInputElement>('f-from')?.value,
      to: $<HTMLInputElement>('f-to')?.value,
    });
    if ('unsupported' in range) {
      blank(range.unsupported);
      noPill();
      return;
    }
    const pick = (id: string) => {
      const v = $<HTMLSelectElement>(id)?.value;
      return v && v !== '*' ? v : undefined;
    };
    const q: CrmQuery = {
      dataSet,
      ...range,
      segment: pick('f-seg'),
      favouriteGame: pick('f-game'),
      vipTier: pick('f-tier'),
      campaignResponse: pick('f-resp'),
    };
    const failed = (err: unknown) => {
      if (err instanceof Unauthenticated) {
        location.reload();
      }
      return { error: (err as Error).message };
    };
    // Segment and Favourite Product apply to the cards too (the summary takes them). A VIP Tier or
    // Campaign Response pick goes to the bridge, which says why it can't apply it; the cards aren't
    // asked for, so none shows an unfiltered number under the pick.
    const refused = Boolean(q.vipTier || q.campaignResponse);
    const [crm, summary] = await Promise.all([
      api.crm(q).catch(failed),
      refused
        ? undefined
        : api.ceoSummary({ dataSet, ...range, segment: q.segment, favouriteGame: q.favouriteGame }).catch(failed),
    ]);
    if (mine !== seq) {
      return; // a newer filter change or data-set switch is already loading
    }
    if (!summary) {
      blank('error' in crm ? `Could not load: ${crm.error}` : 'These figures can’t be filtered by this pick yet.');
      noPill();
      return;
    }
    if ('error' in summary) {
      figuresNone(`Could not load: ${summary.error}`);
      setStatusPills(undefined, pane('crm') ?? undefined);
    } else {
      setStatusPills(summary, pane('crm') ?? undefined);
      drawFigures(summary.results);
    }
    if ('error' in crm) {
      for (const ids of Object.values(CRM_PANELS)) {
        for (const id of ids) {
          none(id, `Could not load: ${crm.error}`);
        }
      }
      noHealth();
      noTrend(`Could not load: ${crm.error}`);
      return;
    }
    // A panel the bridge lists has no data; the others are health's, Unavailable only without it.
    for (const [panel, ids] of Object.entries(CRM_PANELS) as [CrmPanelId, string[]][]) {
      const missingInputs =
        crm.unavailable[panel] ?? (crm.health.status === 'unavailable' ? crm.health.missingInputs : undefined);
      if (missingInputs) {
        const why = reasonOf({ status: 'unavailable', basis: null, missingInputs });
        for (const id of ids) {
          none(id, why);
        }
      }
    }
    drawHealth(crm.health);
    drawActions(crm.health, 'error' in summary ? undefined : summary.results);
    drawRetention(crm);
  }

  let started = false;
  return {
    init() {
      started = true;
      for (const k of ['s-0', 's-1', 's-2']) {
        subs[k] = $(k)?.textContent ?? '';
      }
      subs.baseline = sectionHead('n-4')?.textContent ?? '';
      subs.actions = sectionHead('al-0')?.textContent ?? '';
      $('f-seg')?.setAttribute('title', SEGMENT_NOTE);
      $('f-game')?.setAttribute('title', FAVOURITE_NOTE);
      startOnLastThirtyDays($<HTMLSelectElement>('f-date'));
      for (const head of pane('crm')?.querySelectorAll('.section-head') ?? []) {
        if (head.querySelector('h3')?.textContent === 'Definitions') {
          head.querySelector('span')?.setAttribute('title', DEFINITIONS_NOTE);
        }
      }
      for (const id of ['f-date', 'f-from', 'f-to', 'f-seg', 'f-geo', 'f-game', 'f-tier', 'f-resp']) {
        $(id)?.addEventListener('change', () => void load());
      }
      void load();
    },
    /** Loads again: at once blank for another data set, quietly for a periodic refresh. */
    reload(quiet = false) {
      if (started) {
        void load(quiet);
      }
    },
  };
}

/** "● Data through …" in the pane headers (every pane but those with their own data, or only
 *  `root`'s), from gold's last finished refresh. */
export function setStatusPills(s: Snapshot | undefined, root?: Element) {
  let text = '● Data time unknown';
  let stale = true;
  if (s?.dataAsOf) {
    const at = new Date(s.dataAsOf);
    const date = at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZoneName: 'longOffset' });
    text = `● Data through ${date} · ${time}`;
    stale = s.freshness.status !== 'fresh';
    if (s.freshness.status === 'stale') {
      const mins = Math.round((Date.now() - at.getTime()) / 60_000);
      text += ` · ${mins} min behind`;
    }
  }
  const pills = root
    ? root.querySelectorAll<HTMLElement>('.status-pill')
    : document.querySelectorAll<HTMLElement>(`[data-pane]${OWN_PILL} .status-pill`);
  for (const pill of pills) {
    pill.textContent = text;
    pill.classList.toggle('stale', stale);
  }
}

export function setSideNote(dataSource: string, environment: string) {
  const note = document.querySelector('.side-note p');
  if (note) {
    note.textContent = `${dataSource} (${environment}). Hover or tap a ? to see what a figure counts, or why it is Unavailable.`;
  }
}
